import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { images } from '../data/translations';

export default function Hero() {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const rotations = t.hero.rotations;
  const heroImages = images.heroImages;
  const timerRef = useRef(null);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % rotations.length);
        setFade(true);
      }, 500);
    }, 5000);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [rotations.length]);

  // Reset when language changes
  useEffect(() => {
    setActiveIndex(0);
    setFade(true);
    startTimer();
  }, [rotations]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Dynamic background images — one per rotation */}
      {heroImages.map((img, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[800ms] ease-in-out ${
            i === activeIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className="absolute inset-0 animate-ken-burns"
            style={{
              backgroundImage: `url(${img})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        </div>
      ))}

      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-[#1A1A2E]/55" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-20">
        {/* Rotating headline */}
        <h1
          data-testid="hero-headline"
          className={`text-4xl md:text-5xl lg:text-6xl font-800 leading-tight tracking-tight mb-8 min-h-[3.5em] flex items-center justify-center transition-all duration-500 ${
            fade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          {rotations[activeIndex]}
        </h1>

        {/* Progress bar indicators */}
        <div data-testid="hero-progress" className="flex justify-center gap-3 mb-10">
          {rotations.map((_, i) => (
            <div key={i} className="h-0.5 w-16 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-[#E71C25]"
                style={
                  i === activeIndex
                    ? { animation: 'progressFill 5s linear forwards' }
                    : i < activeIndex
                    ? { width: '100%' }
                    : { width: '0%' }
                }
              />
            </div>
          ))}
        </div>

        {/* Static subtitle */}
        <p
          data-testid="hero-subtitle"
          className="text-lg md:text-xl text-[#B0B0B0] font-medium tracking-wide"
        >
          {t.hero.subtitle}
        </p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <div className="w-5 h-8 border border-white/30 rounded-full flex justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
}
